import { IBadgeList } from '../../indigofair/badges/IBadgeList';
import { ICartItemIssue } from '../../indigofair/data/ICartItemIssue';
import { IFaireMoney } from '../../indigofair/data/IFaireMoney';
import { IItemCustomization } from '../../indigofair/data/IItemCustomization';
import { IItemPromoPrice } from '../../indigofair/data/IItemPromoPrice';
import { ITaxDetail } from '../../indigofair/data/ITaxDetail';

export namespace ICartItem {
  /**
   * Return an instance of ICartItem with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<ICartItem>): ICartItem =>
    ({
      customizations: [],
      duties_and_taxes_estimates: [],
      cart_item_issues: [],
      settings_to_assign_on_view: [],
      ...(partial ?? {}),
    }) as ICartItem;
}

export interface ICartItem {
  token: string | undefined;
  brand_token: string | undefined;
  product_token: string | undefined;
  product_option_token: string | undefined;
  product_collection_token: string | undefined;
  /**
   * @deprecated
   */
  price_cents: number | undefined;
  quantity: number | undefined;
  includes_tester: boolean | undefined;
  /**
   * @deprecated
   */
  tester_price_cents: number | undefined;
  tester_price: IFaireMoney | undefined;
  tester_promo_price: IItemPromoPrice | undefined;
  /**
   * @protobufOption (faire.common.diffIgnore) = IF_NOT_NULL
   */
  added_to_cart_at: number | undefined;
  /**
   * @protobufOption (faire.common.diffIgnore) = IF_NOT_NULL
   */
  checked_out_at: number | undefined;
  /**
   * @deprecated
   */
  saved_for_later: boolean | undefined;
  customizations: Array<IItemCustomization>;
  /**
   * This is just a sum of dutiesAndTaxes.amountCents.
   */
  estimated_duties_and_taxes_cents: number | undefined;
  /**
   * For an international (US -> CA) order, the estimated duties and taxes that could be charged to the retailer.
   * @protobufOption (faire.common.diffAs) = UNORDERED
   */
  duties_and_taxes_estimates: Array<ITaxDetail>;
  /**
   * Is this a preordered cart item
   */
  preordered: boolean | undefined;
  /**
   * This is copied from the product.
   * Shipping window for this pre-order product.
   * This is the start (first day) of the window.
   * The window lasts for expectedShipWindowDays days.
   */
  expected_ship_date: number | undefined;
  /**
   * This is copied from the product.
   * Shipping window for this pre-order product.
   * This is how many days after expectedShipDate the window is.
   * The default is 0, meaning there is no window, and products/orders ship exactly on expectedShipDate.
   */
  expected_ship_window_days: number | undefined;
  /**
   * If clients support, they can group related cart items by groupName.
   * This is a human-readable, displayable-as-is naming for the group of cart items.
   * Cart items with identical names should be grouped.
   * Cart items without group should be grouped together in a default group.
   * Currently this is used to group items by shipping window; identical windows group together and immediates
   * are grouped into a "ASAP" group.
   */
  group_name: string | undefined;
  /**
   * Wholesale price of the item.
   */
  price: IFaireMoney | undefined;
  /**
   * Wholesale promo price of the item.
   */
  promo_price: IItemPromoPrice | undefined;
  /**
   * Retail price / MSRP of the item.
   */
  retail_price: IFaireMoney | undefined;
  /**
   * Wholesale price of prepack / # items in prepack.
   *  Null if not a prepack
   */
  prepack_unit_wholesale_price: IFaireMoney | undefined;
  /**
   * Wholesale price of prepack / # items in prepack.
   *  Null if not a prepack
   */
  prepack_unit_retail_price: IFaireMoney | undefined;
  /**
   * Calculates the total price of this item, i.e. quantityprice.
   */
  total_price: IFaireMoney | undefined;
  /**
   * If present, this is the total price of this cart item including product-level discounts.
   * This is a computed value including roughly defined as the sum of:
   * * (price - product_level_discount)quantity
   * * tester_price - product_level_discount
   */
  total_promo_price: IItemPromoPrice | undefined;
  /**
   * Whether the product has allowSalesWhenOutOfStock
   */
  allow_sales_when_out_of_stock: boolean | undefined;
  /**
   * @protobufOption (faire.common.diffAs) = UNORDERED
   * @protobufOption (faire.common.uniqueKey) = "cartItemToken"
   */
  cart_item_issues: Array<ICartItemIssue>;
  badge_list: IBadgeList | undefined;
  /**
   * @protobufOption (faire.common.diffIgnore) = ALWAYS
   */
  created_at: number | undefined;
  /**
   * @protobufOption (faire.common.diffIgnore) = ALWAYS
   */
  updated_at: number | undefined;
  is_highlighted_product_from_promotional_event: boolean | undefined;
  settings_to_assign_on_view: Array<string>;
}
